/**
 * V3 Socket.IO client hook — Sprint 3.7 (live anomaly updates).
 *
 * Wraps a single shared `socket.io-client` connection so multiple components
 * can subscribe to backend events without each creating their own socket.
 *
 *   import { useV3Socket, useSocketEvent } from '../hooks/useV3Socket';
 *
 *   const { connected } = useV3Socket();         // bootstrap + status
 *   useSocketEvent('anomaly.detected', (p) => …) // bind a handler
 *
 * Backend events (defined in API-EGoalV3/src/realtime/notificationEmitter.js):
 *   - anomaly.detected
 *   - insight.generated
 *   - brief.ready
 *
 * The socket auto-joins room `concessionaire:<id>` based on the JWT, so
 * callers don't need to subscribe explicitly.
 */
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import config from '../../../config';

function getSocketBaseURL() {
    const host = config.hostname || '';
    return host.replace(/\/api\/?$/, '');
}

let socketInstance = null;
let activeToken = null;

function getOrCreateSocket() {
    if (typeof window === 'undefined') return null;
    const token = window.localStorage ? window.localStorage.getItem('sessiontoken') : null;
    if (!token) {
        if (socketInstance) {
            try { socketInstance.disconnect(); } catch (_) { /* noop */ }
            socketInstance = null;
            activeToken = null;
        }
        return null;
    }

    if (socketInstance && activeToken !== token) {
        try { socketInstance.disconnect(); } catch (_) { /* noop */ }
        socketInstance = null;
    }
    if (socketInstance) return socketInstance;

    activeToken = token;
    socketInstance = io(getSocketBaseURL(), {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        withCredentials: true,
    });

    // The backend's Socket.IO auth middleware rejects bad/expired tokens with
    // an Error whose message starts with "unauthorized". Treat that the same
    // as a 401/403 on REST: trigger session expiry once.
    socketInstance.on('connect_error', (err) => {
        const msg = (err && err.message) ? String(err.message).toLowerCase() : '';
        if (msg.includes('unauthorized')) {
            try {
                const { handleSessionExpired } = require('./sessionExpired');
                if (typeof handleSessionExpired === 'function') handleSessionExpired();
            } catch (_) { /* noop */ }
        }
    });

    return socketInstance;
}

/**
 * Tear down the singleton socket. Called from sessionExpired so the client
 * stops reconnecting with a dead JWT after logout/expiry.
 */
export function disconnectSocket() {
    if (!socketInstance) return;
    try { socketInstance.disconnect(); } catch (_) { /* noop */ }
    socketInstance = null;
    activeToken = null;
}

/**
 * Bootstraps (or reuses) the singleton socket and tracks connection status.
 * Returns { socket, connected }.
 */
export function useV3Socket() {
    const socketRef = useRef(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        const s = getOrCreateSocket();
        if (!s) return undefined;
        socketRef.current = s;
        setConnected(!!s.connected);

        const onConnect = () => setConnected(true);
        const onDisconnect = () => setConnected(false);
        const onError = () => setConnected(false);

        s.on('connect', onConnect);
        s.on('disconnect', onDisconnect);
        s.on('connect_error', onError);

        return () => {
            s.off('connect', onConnect);
            s.off('disconnect', onDisconnect);
            s.off('connect_error', onError);
        };
    }, []);

    return { socket: socketRef.current, connected };
}

/**
 * Subscribe to a Socket.IO event for the lifetime of the calling component.
 * The handler is held in a ref so callers don't have to memoize it.
 */
export function useSocketEvent(eventName, handler) {
    const { socket } = useV3Socket();
    const handlerRef = useRef(handler);

    useEffect(() => { handlerRef.current = handler; }, [handler]);

    useEffect(() => {
        if (!socket || !eventName) return undefined;
        const wrapper = (...args) => {
            const fn = handlerRef.current;
            if (typeof fn === 'function') fn(...args);
        };
        socket.on(eventName, wrapper);
        return () => { socket.off(eventName, wrapper); };
    }, [socket, eventName]);
}

export default useV3Socket;
